import { getStage, LAST_STAGE_ID } from '../data/stages.ts';
import { SUBSPEC_IDS } from '../data/subspecs.ts';
import { createNewSave, pointsSpent } from '../model/save.ts';
import {
  LEVEL_CAP,
  SLOT_IDS,
  SLOT_MAX_RANK,
  TICK_SECONDS,
  type BuildState,
  type SaveData,
  type SlotId,
  type SubSpecId,
} from '../model/types.ts';
import { benchmarkRates } from '../sim/benchmark.ts';
import { awardedClearXp, defaultRules, grantXp, resolveHero, sanitizeRules } from '../sim/build.ts';
import { runCombat } from '../sim/combat.ts';
import { rollFarmDrops } from '../sim/drops.ts';
import { absorbDrops } from '../sim/gear.ts';
import { applyFightResult, cancelDrill, resumePush, stageToFight, startDrill } from '../sim/progress.ts';
import { clampOfflineRates, MAX_FARM_STAGE, SERVER_OFFLINE_CAP_HOURS } from '../sim/rateCaps.ts';
import { mixSeed } from '../sim/rng.ts';

export class AuthorityError extends Error {
  readonly code = 'save_invalid';

  constructor(message = 'save_invalid') {
    super(message);
  }
}

/** Same seeds the local fight uses: combat from the current serial, drops from the next one. */
export function seedsForFight(serial: number, stageId: number): { combat: number; drops: number; nextSerial: number } {
  const current = Math.max(1, Math.floor(serial) || 1);
  return {
    combat: mixSeed(current, stageId),
    drops: mixSeed(current + 1, stageId),
    nextSerial: current + 1,
  };
}

export function ratesForSave(data: SaveData): {
  farmStage: number;
  rates: { goldPerMin: number; xpPerMin: number; dustPerMin: number; dropRollsPerMin: number };
  capHours: number;
} {
  assertLoadout(data);
  const farmStage = Math.max(0, Math.min(MAX_FARM_STAGE, Math.floor(data.progress.farmStage) || 0));
  const capHours = SERVER_OFFLINE_CAP_HOURS;
  if (farmStage <= 0) {
    return {
      farmStage: 0,
      capHours,
      rates: { goldPerMin: 0, xpPerMin: 0, dustPerMin: 0, dropRollsPerMin: 0 },
    };
  }
  const stage = getStage(farmStage);
  const hero = resolveHero({
    level: data.hero.level,
    build: data.hero.build,
    gear: data.gear.equipped,
    rules: data.hero.rules,
  });
  const raw = benchmarkRates({
    hero,
    stage,
    seed: mixSeed(0xc0ffee, farmStage),
    minutes: 5,
  });
  const clamped = clampOfflineRates(farmStage, raw);
  return {
    farmStage,
    capHours,
    rates: {
      goldPerMin: roundRate(clamped.goldPerMin),
      xpPerMin: roundRate(clamped.xpPerMin),
      dustPerMin: roundRate(clamped.dustPerMin),
      dropRollsPerMin: roundRate(clamped.dropRollsPerMin),
    },
  };
}

function roundRate(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.round(value * 10_000) / 10_000;
}

/** Fastest watch speed the client draws. The clear gap uses sim time divided by this. */
export const MAX_WATCH_SPEED = 2;

/**
 * Share of the watch-speed fight that may arrive early. 0.1 means the required
 * wait is 90% of simulated duration / MAX_WATCH_SPEED, so a legitimate 2x
 * client is not rejected for a slightly early post.
 */
export const CLEAR_GAP_SLACK = 0.1;

/** Trailing window for the burst guard. */
export const BURST_WINDOW_MS = 60_000;

/** Pad the client adds on top of a valid retry_after_ms before it auto-retries. */
export const CLEAR_RETRY_PAD_MS = 50;

/** Wait used when retry_after_ms is missing or not a whole number of milliseconds. */
export const CLEAR_RETRY_FALLBACK_MS = 1500;

/** Automatic too_fast retries before the client stops and waits for a tap. */
export const CLEAR_AUTO_RETRIES = 3;

function simulatedSecondsOf(simulatedSeconds: number): number {
  return Number.isFinite(simulatedSeconds) && simulatedSeconds > 0 ? simulatedSeconds : TICK_SECONDS;
}

/** Wall-clock duration of this fight drawn at MAX_WATCH_SPEED. */
export function watchMs(simulatedSeconds: number): number {
  return Math.max(1, Math.floor((simulatedSecondsOf(simulatedSeconds) * 1000) / MAX_WATCH_SPEED));
}

/**
 * Minimum gap before the next accepted clear. 10% under the 2x watch duration
 * so a legitimate client can arrive a little early.
 */
export function clearGapMs(simulatedSeconds: number): number {
  return Math.max(1, Math.floor(watchMs(simulatedSeconds) * (1 - CLEAR_GAP_SLACK)));
}

/**
 * Wait the client inserts between repeat farm fights, at every watch speed.
 * The batch cap adds the same wait to clearGapMs. It is not a network round trip.
 */
export const FARM_TURNAROUND_MS = 300;

/** Minimum gap for one repeat-farm clear, including the client turnaround. */
export function farmMinGapMs(simulatedSeconds: number): number {
  return clearGapMs(simulatedSeconds) + FARM_TURNAROUND_MS;
}

export function clearRetryWaitMs(retryAfterMs: number | null | undefined): number {
  if (typeof retryAfterMs !== 'number' || !Number.isInteger(retryAfterMs) || retryAfterMs < 0) {
    return CLEAR_RETRY_FALLBACK_MS;
  }
  return retryAfterMs + CLEAR_RETRY_PAD_MS;
}

/** Gap and burst inputs for one simulated clear. No idle benchmark. */
export function paceOf(simulatedSeconds: number): { minGapMs: number; watchMs: number } {
  return { minGapMs: clearGapMs(simulatedSeconds), watchMs: watchMs(simulatedSeconds) };
}

export interface ClearStamp {
  at: number;
  watchMs: number;
}

/**
 * Charged 2x time of accepted clears that overlaps the trailing minute,
 * plus this clear's own watch time (capped at the window).
 */
export function burstLoadMs(stamps: readonly ClearStamp[], now: number, incomingWatchMs: number): number {
  const windowStart = now - BURST_WINDOW_MS;
  let sum = Math.min(Math.max(0, Math.floor(incomingWatchMs)), BURST_WINDOW_MS);
  for (const stamp of stamps) {
    const lo = Math.max(stamp.at - stamp.watchMs, windowStart);
    const hi = Math.min(stamp.at, now);
    if (hi > lo) sum += hi - lo;
  }
  return sum;
}

export type Admit =
  | { ok: true }
  | { ok: false; retryAfterMs: number };

/**
 * A clear is admitted when the 2x gap has elapsed and the trailing minute
 * does not already hold more than 60s of 2x watch time. `sessionStartedAt`
 * anchors the first clear so a script cannot mint before a 2x fight could
 * have finished. Later clears anchor on the previous accept.
 */
export function admitClear(
  stamps: readonly ClearStamp[],
  now: number,
  simulatedSeconds: number,
  sessionStartedAt: number | null,
): Admit {
  const gap = clearGapMs(simulatedSeconds);
  const watch = watchMs(simulatedSeconds);
  const anchor = stamps.length > 0 ? stamps[stamps.length - 1]!.at : sessionStartedAt;
  const passes = (at: number): boolean => {
    if (anchor !== null && at < anchor + gap) return false;
    return burstLoadMs(stamps, at, watch) <= BURST_WINDOW_MS;
  };
  if (passes(now)) return { ok: true };
  let lo = 0;
  let hi = BURST_WINDOW_MS + gap;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (passes(now + mid)) hi = mid;
    else lo = mid + 1;
  }
  return { ok: false, retryAfterMs: Math.max(1, lo) };
}

export interface ClearResult {
  data: SaveData;
  outcome: 'victory' | 'defeat';
  gold: number;
  xp: number;
  dust: number;
  dropRolls: number;
  levelsGained: number;
  farmStage: number;
  /** Simulated seconds of this fight (`ticks * TICK_SECONDS`). */
  seconds: number;
  stageId: number;
}

/**
 * Re-sim the stage the server save is actually on.
 * A reported id that is not that stage is refused and does not consume the serial.
 */
export function validateClear(data: SaveData, reportedStageId: number): ClearResult {
  const stageId = stageToFight(data.progress);
  if (!Number.isInteger(reportedStageId) || reportedStageId !== stageId) throw new AuthorityError();
  assertLoadout(data);
  const stage = getStage(stageId);
  const seeds = seedsForFight(data.idle.fightSerial, stage.id);
  const hero = resolveHero({
    level: data.hero.level,
    build: data.hero.build,
    gear: data.gear.equipped,
    rules: data.hero.rules,
  });
  const result = runCombat({ hero, stage, seed: seeds.combat, recordLog: false });
  const next = structuredClone(data);
  next.idle.fightSerial = seeds.nextSerial;
  const victory = result.outcome === 'victory';
  let dustGained = 0;
  const farmId = next.progress.farmStage;
  const awardedXp = victory
    ? awardedClearXp({
        stageId: stage.id,
        isWall: stage.isWall,
        clearXp: result.xp,
        heroLevel: next.hero.level,
        bestCleared: next.progress.bestCleared,
        farmStageId: farmId,
        farmClearXp: farmId >= 1 ? getStage(farmId).clearXp : 0,
      })
    : 0;
  if (victory) {
    next.currencies.gold += result.gold;
    next.currencies.forgeDust += result.dust;
    dustGained += result.dust;
    const leveled = grantXp(next.hero.level, next.hero.xp, next.hero.unspentPoints, awardedXp);
    next.hero.level = leveled.level;
    next.hero.xp = leveled.xp;
    next.hero.unspentPoints = leveled.unspent;
    if (result.dropRolls > 0) {
      const rolled = rollFarmDrops({
        count: result.dropRolls,
        tier: stage.tier,
        pity: next.gear.pity,
        seed: seeds.drops,
        startingId: next.gear.nextItemId,
      });
      next.gear.nextItemId = rolled.nextId;
      next.gear.pity = rolled.pity;
      const bag = absorbDrops(next, rolled.items);
      next.gear.bag = bag.bag;
      next.currencies.gold += bag.gold;
      next.currencies.forgeDust += bag.forgeDust;
      dustGained += bag.forgeDust;
    }
  }
  next.progress = applyFightResult(next.progress, stage, victory, LAST_STAGE_ID);
  if (victory && next.hero.level !== data.hero.level) {
    const gained = next.hero.level - data.hero.level;
    const points = `Level ${next.hero.level}. ${gained} sub-spec point${gained === 1 ? '' : 's'} to spend.`;
    next.progress.notice = next.progress.notice ? `${points} ${next.progress.notice}` : points;
  }
  assertLoadout(next);
  return {
    data: next,
    outcome: victory ? 'victory' : 'defeat',
    gold: victory ? result.gold : 0,
    xp: victory ? awardedXp : 0,
    dust: dustGained,
    dropRolls: victory ? result.dropRolls : 0,
    levelsGained: next.hero.level - data.hero.level,
    farmStage: next.progress.farmStage,
    seconds: result.seconds,
    stageId: stage.id,
  };
}

/**
 * A legal braid is exactly three different sub-specs.
 * Points in hand plus points spent equal the hero's level: one point per level, including level 1.
 * Re-braid is free and only refunds ranks. It does not drop a spec or charge gold.
 */
export function assertLoadout(data: SaveData): void {
  const build = data.hero.build;
  const chosen: SubSpecId[] = [];
  for (const slot of SLOT_IDS) {
    const id = build[slot];
    if (id === null || !SUBSPEC_IDS.includes(id)) throw new AuthorityError();
    chosen.push(id);
  }
  if (new Set(chosen).size !== chosen.length) throw new AuthorityError();
  if (!Number.isInteger(data.hero.level) || data.hero.level < 1 || data.hero.level > LEVEL_CAP) throw new AuthorityError();
  if (!Number.isInteger(data.hero.unspentPoints) || data.hero.unspentPoints < 0) throw new AuthorityError();
  for (const slot of SLOT_IDS) {
    const spec = build[slot];
    if (!spec) throw new AuthorityError();
    const rank = build.ranks[spec] ?? 0;
    if (!Number.isInteger(rank) || rank < 0 || rank > SLOT_MAX_RANK[slot]) throw new AuthorityError();
  }
  for (const [id, rank] of Object.entries(build.ranks)) {
    if (!chosen.includes(id as SubSpecId) && (rank ?? 0) > 0) throw new AuthorityError();
  }
  if (pointsSpent(build) + data.hero.unspentPoints !== data.hero.level) throw new AuthorityError();
}

export function spendRank(data: SaveData, slot: SlotId): SaveData {
  if (!SLOT_IDS.includes(slot)) throw new AuthorityError();
  assertLoadout(data);
  const next = structuredClone(data);
  const spec = next.hero.build[slot];
  if (!spec) throw new AuthorityError();
  if (next.hero.unspentPoints < 1) throw new AuthorityError();
  const rank = next.hero.build.ranks[spec] ?? 0;
  if (rank >= SLOT_MAX_RANK[slot]) throw new AuthorityError();
  next.hero.build.ranks[spec] = rank + 1;
  next.hero.unspentPoints -= 1;
  assertLoadout(next);
  return next;
}

export interface RankAllocation {
  slot: SlotId;
  count: number;
}

/**
 * Applies every allocation or throws and leaves the caller's save untouched.
 * Caps, unspent points, and the equipped sub-spec are the same checks as one spend.
 */
export function spendRanks(data: SaveData, spends: readonly RankAllocation[]): SaveData {
  if (!Array.isArray(spends) || spends.length === 0 || spends.length > SLOT_IDS.length) throw new AuthorityError();
  const seen = new Set<SlotId>();
  for (const spend of spends) {
    if (!spend || typeof spend !== 'object') throw new AuthorityError();
    const slot: SlotId = spend.slot;
    if (!SLOT_IDS.includes(slot) || seen.has(slot)) throw new AuthorityError();
    seen.add(slot);
    const count: number = spend.count;
    if (typeof count !== 'number' || !Number.isInteger(count) || count < 1 || count > SLOT_MAX_RANK[slot]) {
      throw new AuthorityError();
    }
  }
  let next = data;
  for (const spend of spends) {
    for (let i = 0; i < spend.count; i += 1) next = spendRank(next, spend.slot);
  }
  return next;
}

/** Body of spend_points. One rank, a batch of ranks, a re-braid, or a spec change. */
export function applySpendAction(data: SaveData, body: Record<string, unknown>): SaveData {
  const action = body.action;
  if (action === 'spend') {
    if (body.ranks !== undefined) return spendRanks(data, readRankAllocations(body.ranks));
    return spendRank(data, body.slot as SlotId);
  }
  if (action === 'rebraid') return rebraidRanks(data);
  if (action === 'set_spec') return setSpec(data, body.slot as SlotId, (body.spec ?? null) as SubSpecId | null);
  throw new AuthorityError();
}

function readRankAllocations(raw: unknown): RankAllocation[] {
  if (!Array.isArray(raw)) throw new AuthorityError();
  return raw.map((entry) => {
    if (!entry || typeof entry !== 'object') throw new AuthorityError();
    const slot = (entry as { slot?: unknown }).slot;
    const count = (entry as { count?: unknown }).count;
    if (slot !== 'primary' && slot !== 'secondary' && slot !== 'tertiary') throw new AuthorityError();
    if (typeof count !== 'number') throw new AuthorityError();
    return { slot, count };
  });
}

const REPAIR_FILL: SubSpecId[] = [
  'bulwark',
  'banner',
  'echo',
  ...SUBSPEC_IDS.filter((id) => id !== 'bulwark' && id !== 'banner' && id !== 'echo'),
];

/**
 * Makes a stored braid legal again.
 * Empty or unknown slots are filled, stray rank keys are dropped, and unspent becomes level minus spent.
 * An explicit rank of 0 stays on the spec. A missing key is not filled in.
 * If the kept ranks already exceed the level, every rank is cleared and the points are refunded.
 * Running it twice leaves the same braid.
 */
export function repairLoadout(data: SaveData): SaveData {
  const next = structuredClone(data);
  let level = next.hero.level;
  if (!Number.isInteger(level) || level < 1) level = 1;
  if (level > LEVEL_CAP) level = LEVEL_CAP;
  next.hero.level = level;

  const slots = {} as Record<SlotId, SubSpecId>;
  const used = new Set<SubSpecId>();
  for (const slot of SLOT_IDS) {
    const raw = next.hero.build?.[slot];
    const known = typeof raw === 'string' && SUBSPEC_IDS.includes(raw as SubSpecId) && !used.has(raw as SubSpecId);
    const spec = known ? (raw as SubSpecId) : REPAIR_FILL.find((id) => !used.has(id));
    if (!spec) throw new AuthorityError();
    slots[slot] = spec;
    used.add(spec);
  }

  const incoming = next.hero.build?.ranks;
  const ranks: BuildState['ranks'] = {};
  let spent = 0;
  if (incoming && typeof incoming === 'object' && !Array.isArray(incoming)) {
    for (const slot of SLOT_IDS) {
      const spec = slots[slot];
      const raw = incoming[spec];
      if (typeof raw !== 'number' || !Number.isInteger(raw) || raw < 0) continue;
      const rank = Math.min(raw, SLOT_MAX_RANK[slot]);
      ranks[spec] = rank;
      spent += rank;
    }
  }
  if (spent > level) {
    for (const id of Object.keys(ranks)) delete ranks[id as SubSpecId];
    spent = 0;
  }
  next.hero.build = { primary: slots.primary, secondary: slots.secondary, tertiary: slots.tertiary, ranks };
  next.hero.unspentPoints = level - spent;
  return next;
}

export function rebraidRanks(data: SaveData): SaveData {
  const next = repairLoadout(data);
  for (const slot of SLOT_IDS) {
    const spec = next.hero.build[slot];
    if (!spec) continue;
    next.hero.unspentPoints += next.hero.build.ranks[spec] ?? 0;
    next.hero.build.ranks[spec] = 0;
  }
  next.hero.rules = defaultRules(next.hero.build);
  assertLoadout(next);
  return next;
}

export function setSpec(data: SaveData, slot: SlotId, spec: SubSpecId | null): SaveData {
  if (!SLOT_IDS.includes(slot)) throw new AuthorityError();
  if (spec === null || !SUBSPEC_IDS.includes(spec)) throw new AuthorityError();
  assertLoadout(data);
  const next = structuredClone(data);
  const build: BuildState = next.hero.build;
  for (const other of SLOT_IDS) {
    if (other !== slot && build[other] === spec) throw new AuthorityError();
  }
  const current = build[slot];
  if (current && current !== spec) {
    next.hero.unspentPoints += build.ranks[current] ?? 0;
    delete build.ranks[current];
  }
  build[slot] = spec;
  if (build.ranks[spec] === undefined) build.ranks[spec] = 0;
  next.hero.rules = sanitizeRules(build, next.hero.rules);
  if (next.hero.rules.length === 0) next.hero.rules = defaultRules(build);
  assertLoadout(next);
  return next;
}

/** Fixed spend, respec, and fusion inputs so Node and Deno agree. */
export function progressionParity(): {
  primary: string;
  unspent: number;
  rank: number;
  fusion: string | null;
  strip: number;
  pool: number;
} {
  let data = createNewSave();
  data = setSpec(data, 'primary', 'duelist');
  data = setSpec(data, 'tertiary', 'grave');
  data = spendRank(data, 'primary');
  const hero = resolveHero({
    level: data.hero.level,
    build: data.hero.build,
    gear: data.gear.equipped,
    rules: data.hero.rules,
  });
  return {
    primary: data.hero.build.primary ?? '',
    unspent: data.hero.unspentPoints,
    rank: data.hero.build.ranks.duelist ?? 0,
    fusion: hero.fusionName,
    strip: hero.bonusStrip,
    pool: hero.resourcePool,
  };
}

export type FrontierAction =
  | { action: 'push' }
  | { action: 'drill'; stageId: number }
  | { action: 'cancel_drill' };

/**
 * Server-owned return to the frontier.
 * Push clears a drill and leaves farm mode. Drill may only target a cleared
 * non-wall stage above the farm stage and at or under the farm cap.
 * The fight serial is left alone, so this does not skip the clear gap.
 */
export function applyFrontier(data: SaveData, action: FrontierAction): SaveData {
  const next = structuredClone(data);
  if (action.action === 'push') {
    next.progress = resumePush(cancelDrill(next.progress));
    return next;
  }
  if (action.action === 'cancel_drill') {
    next.progress = cancelDrill(next.progress);
    return next;
  }
  const stageId = action.stageId;
  if (!Number.isInteger(stageId) || stageId < 1 || stageId > MAX_FARM_STAGE) throw new AuthorityError();
  if (stageId > next.progress.bestCleared || stageId <= next.progress.farmStage) throw new AuthorityError();
  const stage = getStage(stageId);
  if (stage.isWall) throw new AuthorityError();
  const drilled = startDrill(next.progress, stageId, false);
  if (drilled.drillStage !== stageId) throw new AuthorityError();
  next.progress = drilled;
  return next;
}

/** Fixed inputs so Node and Deno can compare one benchmark. */
export function parityPayload(): { goldPerMin: number; outcome: string; farmStage: number } {
  const data = createNewSave();
  const first = validateClear(data, 1);
  const second = validateClear(first.data, 1);
  const third = validateClear(second.data, 1);
  const rates = ratesForSave(third.data);
  return {
    goldPerMin: rates.rates.goldPerMin,
    outcome: third.outcome,
    farmStage: third.data.progress.farmStage,
  };
}
